import { ExtractorResponse } from "../../../../common/entities/type";
import { KeragonJobResponse } from "../../../../common/entities/keragon";

/**
 * Poll loop for the Keragon lab import (backend doc: ~2–3 s interval, 2–3 min
 * timeout). Results are kept ~1 h keyed by patient identity, so an earlier
 * search's "Done" can still be sitting there when a new one is triggered; a
 * result created before this trigger (minus a clock-skew allowance) is
 * treated as "Pending" until the fresh one replaces it.
 */
export const KERAGON_POLL_INTERVAL_MS = 2500;
export const KERAGON_POLL_TIMEOUT_MS = 3 * 60 * 1000;
export const KERAGON_CLOCK_SKEW_MS = 60 * 1000;

export class KeragonPollError extends Error {
  constructor(public reason: "failed" | "timeout" | "cancelled", message: string) {
    super(message);
  }
}

/** True when `createdAt` predates the trigger — i.e. a previous search. */
export const isStaleResult = (
  createdAt: string | undefined,
  triggeredAt: number,
  skewMs: number = KERAGON_CLOCK_SKEW_MS
) => {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  return !Number.isNaN(created) && created < triggeredAt - skewMs;
};

type PollOptions = {
  fetchStatus: () => Promise<KeragonJobResponse | null>;
  triggeredAt: number;
  isCancelled: () => boolean;
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
};

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export const pollKeragonResult = async ({
  fetchStatus,
  triggeredAt,
  isCancelled,
  intervalMs = KERAGON_POLL_INTERVAL_MS,
  timeoutMs = KERAGON_POLL_TIMEOUT_MS,
  sleep = defaultSleep,
  now = Date.now,
}: PollOptions): Promise<ExtractorResponse | null> => {
  const deadline = now() + timeoutMs;
  while (now() < deadline) {
    if (isCancelled()) throw new KeragonPollError("cancelled", "Cancelled");
    const job = await fetchStatus();
    if (isCancelled()) throw new KeragonPollError("cancelled", "Cancelled");
    if (job && !isStaleResult(job.createdAt, triggeredAt)) {
      if (job.status === "Done") return job.result ?? null;
      if (job.status === "Failed") {
        throw new KeragonPollError("failed", job.error || "Keragon extraction failed");
      }
    }
    await sleep(intervalMs);
  }
  throw new KeragonPollError("timeout", "Timeout waiting for Keragon result");
};
